import { Component, computed, inject, input } from "@angular/core";
import { PlanOptions } from "@bitwarden/web-vault/app/models/types/subscription.type";
import { UserStoreService } from "@bitwarden/web-vault/app/services/store/user/user.store.service";
import { HelperTrialRemaining } from "@bitwarden/web-vault/app/shared/utils/helper-trial-remaining";

@Component({
  selector: "app-change-plan-dialog",
  templateUrl: "./change-plan-dialog.component.html",
})
export class ChangePlanDialogComponent {
  private userStoreService = inject(UserStoreService);

  closeDialog = input.required<() => void>();
  newPlanType = input.required<"annual" | "monthly">(); // The plan user is switching to
  marketLabel = input<string>(""); // Selected market label (e.g. "Aussie Banks")
  selectedPaymentMethod = input.required<"add" | "skip">(); // Payment method selected in plan-details
  onConfirm = input.required<() => void>(); // Action to execute on confirm
  onCancel = input.required<() => void>(); // Action to execute on cancel (reopen plan-details)

  protected readonly planOptions = PlanOptions;
  private trialHelper = new HelperTrialRemaining();

  // Current plan info from user's subscription
  protected currentPlan = computed(() => {
    // null before the subscription loads / after logout: falls through to monthly below.
    const subscription = this.userStoreService.subscription.activeSubscription();
    const currentCycle = subscription?.paymentCycle; // "year" or "month"

    if (currentCycle === "year") {
      return {
        type: "annual" as const,
        plan: this.planOptions.premiumAnnual,
      };
    } else {
      return {
        type: "monthly" as const,
        plan: this.planOptions.premiumMonthly,
      };
    }
  });

  // New plan info based on input
  protected newPlan = computed(() => {
    const type = this.newPlanType();
    const plan =
      type === "annual" ? this.planOptions.premiumAnnual : this.planOptions.premiumMonthly;
    return {
      type,
      plan,
    };
  });

  // Calculate new billing date (assuming immediate switch)
  protected newBillingDate = computed(() =>
    this.trialHelper.formatBillingDate(
      this.trialHelper.getNextBillingDate(this.userStoreService.subscription.activeSubscription()),
    ),
  );

  protected close(): void {
    this.closeDialog()();
    const cancelFn = this.onCancel();
    if (cancelFn) {
      cancelFn();
    }
  }

  protected handleConfirm(): void {
    // Just close the dialog without calling onCancel to avoid reopening plan-details
    this.closeDialog()();

    const confirmFn = this.onConfirm();
    if (confirmFn) {
      confirmFn();
    }
  }
}
